import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

const brandApi = createApi({
  reducerPath: "brandApi",
  baseQuery: prepareBaseQuery("/brands/"),
  tagTypes: ["BrandConfig", "BrandConfigHistory"],
  endpoints: (builder) => ({
    // Public config for the website (BrandContext). The backend leaves out
    // its private fields (for example the e-transfer security Q&A).
    getBrandBySlug: builder.query({
      query: (slug) => `${slug}/config`,
    }),
    // Admin only (Bearer token): the complete stored config, private fields
    // included. The section editor reads this and saves one section at a time.
    getBrandConfigFull: builder.query({
      query: (slug) => `${slug}/config/full`,
      providesTags: ["BrandConfig"],
    }),
    // The section table (key, tier, label, editor) and the fixed key lists the
    // form editors render from. No config values.
    getBrandConfigSections: builder.query({
      query: (slug) => `${slug}/config/sections`,
    }),
    // Save ONE section. The server validates, refuses to drop stored keys
    // unless allowRemove is exactly true (400 KEYS_REMOVED with `removed`),
    // and keeps the previous config in history.
    patchBrandConfigSection: builder.mutation({
      query: ({ slug, section, value, allowRemove }) => ({
        url: `${slug}/config/section/${section}`,
        method: "PATCH",
        body: allowRemove === true ? { value, allowRemove: true } : { value },
      }),
      invalidatesTags: ["BrandConfig", "BrandConfigHistory"],
    }),
    // The last ten configs, newest first, summaries only.
    getBrandConfigHistory: builder.query({
      query: (slug) => `${slug}/config/history`,
      providesTags: ["BrandConfigHistory"],
    }),
    // Put a history snapshot back as the whole config (itself undoable).
    restoreBrandConfig: builder.mutation({
      query: ({ slug, index }) => ({
        url: `${slug}/config/restore`,
        method: "POST",
        body: { index },
      }),
      invalidatesTags: ["BrandConfig", "BrandConfigHistory"],
    }),
    // Whole-document PUT. Kept for scripts and tests; the admin UI no longer
    // calls it (section editor, 2026-09-26).
    updateBrandConfig: builder.mutation({
      query: ({ slug, config }) => ({
        url: `${slug}/config`,
        method: "PUT",
        body: { config },
      }),
      invalidatesTags: ["BrandConfig", "BrandConfigHistory"],
    }),
  }),
});

export const {
  useGetBrandBySlugQuery,
  useGetBrandConfigFullQuery,
  useGetBrandConfigSectionsQuery,
  usePatchBrandConfigSectionMutation,
  useGetBrandConfigHistoryQuery,
  useRestoreBrandConfigMutation,
  useUpdateBrandConfigMutation,
} = brandApi;

export default brandApi;
export { brandApi };
